import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, Printer, Package } from 'lucide-react';
import { useReactToPrint } from 'react-to-print';
import axios from 'axios';
import PrintSettings from './PrintSettings';
import DeliveryPointsList from './DeliveryPointsList';
import PrintTemplate from './PrintTemplate';

function PrintDeliveriesContent({ 
  userId, 
  isAdminView = false, 
  isAllUsers = false,
  adminUserId = null,
  sellerName = '',
  onBackClick 
}) {
  const [selectedDeliveryPoints, setSelectedDeliveryPoints] = useState([]);
  const [deliveryPoints, setDeliveryPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [printerType, setPrinterType] = useState('a4');
  const printRef = useRef();

  useEffect(() => {
    fetchDeliveryPoints();
  }, [userId, isAllUsers, adminUserId]);

  async function fetchDeliveryPoints() {
    setLoading(true);
    try {
      let response;
      
      if (isAllUsers && adminUserId) {
        // Fetch all deliveries for all users under this admin
        response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/delivery_points/provider/${adminUserId}`
        );
      } else if (isAdminView) {
        // Fetch deliveries for specific user
        response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/user/${userId}`
        );
      } else {
        // Regular user view
        response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/user/${userId}`
        );
      }

      const points = isAllUsers 
        ? response.data // Array from provider endpoint
        : (response.data.delivery_points || []); // Object from user endpoint
      
      setDeliveryPoints(points);
    } catch (error) {
      console.error('Error fetching delivery points:', error);
    } finally {
      setLoading(false);
    }
  }

  function handleSelectionChange(dp) {
    setSelectedDeliveryPoints(prev => {
      if (prev.some(item => item.id === dp.id)) {
        return prev.filter(item => item.id !== dp.id);
      } else {
        return [...prev, dp];
      }
    });
  }

  function handleSelectAll() {
    if (selectedDeliveryPoints.length === deliveryPoints.length) {
      setSelectedDeliveryPoints([]);
    } else {
      setSelectedDeliveryPoints([...deliveryPoints]);
    }
  }

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: printerType === 'thermal' 
      ? `
        @page {
          size: 80mm auto;
          margin: 0;
        }
        @media print {
          body { margin: 0; }
        }
      `
      : `
        @page {
          size: A4 portrait;
          margin: 10mm;
        }
        @media print {
          body { margin: 0; }
        }
      `
  });

  const allSelected = deliveryPoints.length > 0 && selectedDeliveryPoints.length === deliveryPoints.length;

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Fixed Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <button
              onClick={onBackClick}
              className="flex items-center gap-2 text-slate-700 hover:text-[#B1336E] transition-colors duration-200 font-montserrat"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm sm:text-base">Volver</span>
            </button>
            <div className="text-center">
              <h1 className="text-lg sm:text-xl font-extrabold text-[#B1336E] font-montserrat">
                Rotulado
              </h1>
              {isAdminView && sellerName && (
                <p className="text-xs text-slate-600 font-montserrat">
                  {sellerName}
                </p>
              )}
            </div>
            <div className="w-20"></div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          
          {/* Instructions Card */}
          <div className="bg-gradient-to-r from-[#B1336E]/10 to-[#E6B43C]/10 border-l-4 border-[#B1336E] rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <Package className="w-5 h-5 text-[#B1336E] flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-base font-bold text-[#B1336E] font-montserrat mb-1">
                  Selecciona las entregas a imprimir
                </h2>
                <p className="text-sm text-slate-700 font-montserrat">
                  Escoge las entregas que deseas imprimir en formato de etiquetas
                </p>
              </div>
            </div>
          </div>

          {/* Printer Settings */}
          <PrintSettings 
            printerType={printerType}
            onPrinterTypeChange={setPrinterType}
          />

          {/* Delivery Points List */}
          <DeliveryPointsList
            deliveryPoints={deliveryPoints}
            selectedDeliveryPoints={selectedDeliveryPoints}
            onSelectionChange={handleSelectionChange}
            onSelectAll={handleSelectAll}
            allSelected={allSelected}
            loading={loading}
          />
        </div>
      </div>

      {/* Fixed Footer - Print Button */}
      <div className="flex-shrink-0 bg-white border-t border-slate-200 shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <button
            onClick={handlePrint}
            disabled={selectedDeliveryPoints.length === 0}
            className="w-full bg-gradient-to-r from-[#B1336E] to-[#E6B43C] text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed font-montserrat flex items-center justify-center gap-2"
          >
            <Printer className="w-5 h-5" />
            <span>
              Imprimir {selectedDeliveryPoints.length > 0 && `(${selectedDeliveryPoints.length})`}
            </span>
          </button>
        </div>
      </div>

      {/* Hidden Print Content */}
      <div style={{ display: 'none' }}>
        <PrintTemplate
          ref={printRef}
          deliveryPoints={selectedDeliveryPoints}
          printerType={printerType}
        />
      </div>
    </div>
  );
}

export default PrintDeliveriesContent;